import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';

interface CubicleConfig {
  cubicle_id: string;
  x_min: number;
  x_max: number;
  y_min: number;
  y_max: number;
  employee_id: string;
}

interface Employee {
  id: string;
  name: string;
  email: string;
}

export function AdminCubicleConfig() {
  const [cubicles, setCubicles] = useState<CubicleConfig[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedCubicle, setSelectedCubicle] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cubicleRes, empRes] = await Promise.all([
          apiClient.get('/api/admin/config/cubicles'),
          apiClient.get('/api/admin/employees')
        ]);
        setCubicles(cubicleRes.data.cubicles);
        setEmployees(empRes.data.employees);
        setError('');
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleEmployeeChange = (cubicleId: string, employeeId: string) => {
    setPendingChanges((prev) => ({ ...prev, [cubicleId]: employeeId }));
  };

  const handleSave = async () => {
    const updates = Object.entries(pendingChanges).map(([cubicle_id, employee_id]) => ({
      cubicle_id,
      employee_id,
    }));

    if (updates.length === 0) {
      setSuccess('No changes to save');
      setTimeout(() => setSuccess(''), 3000);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiClient.put('/api/admin/config/cubicles', updates);
      setCubicles(response.data.cubicles);
      setPendingChanges({});
      setSelectedCubicle(null);
      setSuccess('Configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingChanges({});
    setSelectedCubicle(null);
  };

  const getEmployeeName = (employeeId: string): string => {
    const emp = employees.find((e) => e.id === employeeId);
    return emp ? emp.name : 'Unassigned';
  };

  const getCurrentValue = (cubicle: CubicleConfig): string => {
    return pendingChanges[cubicle.cubicle_id] ?? cubicle.employee_id;
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  const getCubicleColor = (cubicleId: string) => {
    const colors = [
      { bg: 'rgba(16, 185, 129, 0.3)', border: '#10B981' },
      { bg: 'rgba(59, 130, 246, 0.3)', border: '#3B82F6' },
      { bg: 'rgba(245, 158, 11, 0.3)', border: '#F59E0B' },
      { bg: 'rgba(139, 92, 246, 0.3)', border: '#8B5CF6' },
      { bg: 'rgba(236, 72, 153, 0.3)', border: '#EC4899' },
      { bg: 'rgba(6, 182, 212, 0.3)', border: '#06B6D4' },
    ];
    const index = cubicles.findIndex(c => c.cubicle_id === cubicleId);
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIcon}>🗺️</div>
          <div>
            <h1 style={styles.title}>Cubicle Configuration</h1>
            <p style={styles.subtitle}>Map camera regions to employee workstations</p>
          </div>
        </div>
        
        {hasChanges && (
          <div style={styles.headerActions}>
            <button onClick={handleCancel} style={styles.cancelBtn} disabled={saving}>
              Cancel
            </button>
            <button onClick={handleSave} style={styles.saveBtn} disabled={saving}>
              {saving ? '⟳ Saving...' : '💾 Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div style={styles.errorBox}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div style={styles.successBox}>
          <span>✓</span>
          <span>{success}</span>
        </div>
      )}

      <div style={styles.content}>
        {/* Visual Map */}
        <div style={styles.mapSection}>
          <h2 style={styles.sectionTitle}>
            <span>📷</span> Camera View
          </h2>
          <div style={styles.mapCard}>
            <div style={styles.cameraView}>
              {cubicles.map((cubicle) => {
                const color = getCubicleColor(cubicle.cubicle_id);
                const isSelected = selectedCubicle === cubicle.cubicle_id;
                const hasChange = pendingChanges[cubicle.cubicle_id] !== undefined;
                
                return (
                  <div
                    key={cubicle.cubicle_id}
                    style={{
                      ...styles.cubicleRegion,
                      left: `${cubicle.x_min * 100}%`,
                      top: `${cubicle.y_min * 100}%`,
                      width: `${(cubicle.x_max - cubicle.x_min) * 100}%`,
                      height: `${(cubicle.y_max - cubicle.y_min) * 100}%`,
                      backgroundColor: isSelected ? 'rgba(124, 58, 237, 0.4)' : color.bg,
                      borderColor: isSelected ? '#7C3AED' : color.border,
                      boxShadow: isSelected ? '0 0 20px rgba(124, 58, 237, 0.5)' : 'none',
                    }}
                    onClick={() => setSelectedCubicle(cubicle.cubicle_id)}
                  >
                    <div style={styles.cubicleLabel}>{cubicle.cubicle_id}</div>
                    <div style={styles.cubicleEmployee}>
                      {getEmployeeName(getCurrentValue(cubicle))}
                    </div>
                    {hasChange && <div style={styles.changeDot} />}
                  </div>
                );
              })}
              
              {/* Grid overlay */}
              <div style={styles.gridOverlay} />
            </div>
            <div style={styles.mapLegend}>
              <span style={styles.legendIcon}>📷</span>
              <span>Click a region to edit assignment</span>
            </div>
          </div>
        </div>

        {/* Configuration Panel */}
        <div style={styles.configSection}>
          <h2 style={styles.sectionTitle}>
            <span>⚙️</span> Assignments
          </h2>
          
          <div style={styles.configList}>
            {cubicles.map((cubicle) => {
              const color = getCubicleColor(cubicle.cubicle_id);
              const isSelected = selectedCubicle === cubicle.cubicle_id;
              const hasChange = pendingChanges[cubicle.cubicle_id] !== undefined;
              
              return (
                <div
                  key={cubicle.cubicle_id}
                  style={{
                    ...styles.configCard,
                    ...(isSelected ? styles.configCardSelected : {}),
                  }}
                  onClick={() => setSelectedCubicle(cubicle.cubicle_id)}
                >
                  <div style={styles.configHeader}>
                    <div style={{...styles.configDot, background: color.border}} />
                    <span style={styles.configId}>{cubicle.cubicle_id}</span>
                    {hasChange && <span style={styles.modifiedBadge}>Modified</span>}
                  </div>
                  
                  <div style={styles.configCoords}>
                    X: {cubicle.x_min.toFixed(2)} → {cubicle.x_max.toFixed(2)}
                    <br />
                    Y: {cubicle.y_min.toFixed(2)} → {cubicle.y_max.toFixed(2)}
                  </div>
                  
                  <select
                    value={getCurrentValue(cubicle)}
                    onChange={(e) => handleEmployeeChange(cubicle.cubicle_id, e.target.value)}
                    style={styles.select}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">— Unassigned —</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div style={styles.infoBox}>
        <div style={styles.infoIcon}>💡</div>
        <div>
          <strong>How it works</strong>
          <p style={styles.infoText}>
            Each cubicle region is defined by normalized coordinates (0.0 to 1.0) representing 
            the camera's field of view. When a person is detected in a video frame, their position 
            is matched to a cubicle region to identify the employee.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { maxWidth: '1200px', margin: '0 auto' },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
    gap: '16px',
    color: '#6B7280',
  },
  loadingSpinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #E5E7EB',
    borderTopColor: '#7C3AED',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  headerIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.3)',
  },
  title: { fontSize: '28px', fontWeight: 700, color: '#1E1B4B', margin: 0 },
  subtitle: { fontSize: '15px', color: '#6B7280', marginTop: '4px' },
  headerActions: { display: 'flex', gap: '12px' },
  cancelBtn: {
    padding: '12px 24px',
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#4B5563',
    cursor: 'pointer',
  },
  saveBtn: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 18px',
    background: '#FEE2E2',
    borderRadius: '12px',
    color: '#DC2626',
    marginBottom: '20px',
  },
  successBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 18px',
    background: '#D1FAE5',
    borderRadius: '12px',
    color: '#065F46',
    marginBottom: '20px',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '24px',
  },
  mapSection: {},
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '18px',
    fontWeight: 600,
    color: '#1E1B4B',
    marginBottom: '16px',
  },
  mapCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '20px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  },
  cameraView: {
    position: 'relative',
    width: '100%',
    paddingBottom: '75%',
    background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  cubicleRegion: {
    position: 'absolute',
    border: '3px solid',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  cubicleLabel: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'white',
    textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  },
  cubicleEmployee: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.9)',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
    marginTop: '4px',
    maxWidth: '90%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  changeDot: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#F59E0B',
    boxShadow: '0 0 8px rgba(245, 158, 11, 0.6)',
  },
  gridOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
    `,
    backgroundSize: '10% 10%',
    pointerEvents: 'none',
  },
  mapLegend: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '16px',
    fontSize: '13px',
    color: '#6B7280',
  },
  legendIcon: { fontSize: '16px' },
  configSection: {},
  configList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  configCard: {
    padding: '16px',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '2px solid transparent',
  },
  configCardSelected: {
    borderColor: '#7C3AED',
    boxShadow: '0 4px 20px rgba(124, 58, 237, 0.15)',
  },
  configHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  configDot: {
    width: '12px',
    height: '12px',
    borderRadius: '4px',
  },
  configId: { fontSize: '16px', fontWeight: 700, color: '#1E1B4B' },
  modifiedBadge: {
    marginLeft: 'auto',
    fontSize: '10px',
    fontWeight: 600,
    padding: '4px 8px',
    background: '#FEF3C7',
    color: '#D97706',
    borderRadius: '6px',
  },
  configCoords: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '12px',
    fontFamily: 'monospace',
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    outline: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  infoBox: {
    display: 'flex',
    gap: '16px',
    padding: '20px',
    background: '#EFF6FF',
    borderRadius: '16px',
    marginTop: '24px',
  },
  infoIcon: { fontSize: '24px' },
  infoText: { fontSize: '14px', color: '#1E40AF', margin: '8px 0 0', lineHeight: 1.6 },
};
